/**
 * Line Number Gutter
 *
 * IDE-style line numbers in the page's leading margin, rendered OUTSIDE the
 * editor — a sibling layer inside the page Box, not a Lexical plugin. It adds
 * nothing to the editor's DOM or update cycle and can never overlap the text
 * (its own margin layer). Numbers come from `computeBlockLineNumbers(ast)` — the
 * same renderer that writes `content/<file>.md` — so they match the on-disk line
 * numbers. Cmd/Ctrl+G jumps to a line.
 *
 * It still reads each block's vertical position from the editor's rendered DOM
 * (only the editor knows where text wraps), but that runs debounced and off the
 * typing path. Scrolling needs no handler: the layer lives inside the scrolled
 * page, so the numbers move with the text natively.
 */

import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material'
import { type BlockLineInfo, type ContentAST, computeBlockLineNumbers } from '@shared/ast'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

export interface LineNumberGutterProps {
  /** Current author AST — the source of the line-number map (null = nothing open). */
  ast: ContentAST | null
  /** Text direction — picks the gutter side (right for RTL, left for LTR). */
  direction: 'ltr' | 'rtl'
  /** Render only in author edit mode (hidden in reader / read-only). */
  visible: boolean
  /** Scroll to and flash a block id — wired to the editor's `scrollToBlock`. */
  onGoToBlock: (blockId: string) => void
}

const GUTTER_WIDTH = 34 // px column reserved for the digits
// Gap between the text edge and the number column. Kept clear of the tag
// side-bar (TagIndicatorPlugin draws it ~9–12px past the text edge), so numbers
// don't collide with tag indicators.
const GUTTER_GAP = 22
const REPOSITION_DEBOUNCE_MS = 80
const NUMBER_CLASS = 'line-number-gutter-num'

// Inject the number style once.
let stylesInjected = false
function injectStyles() {
  if (stylesInjected) {
    return
  }
  stylesInjected = true

  const style = document.createElement('style')
  style.textContent = `
    .${NUMBER_CLASS} {
      position: absolute;
      font-size: 11px;
      line-height: 1.4;
      color: var(--text-tertiary, #9aa0a6);
      font-variant-numeric: tabular-nums;
      user-select: none;
      pointer-events: none;
      opacity: 0.6;
      white-space: nowrap;
    }
  `
  document.head.appendChild(style)
}

export function LineNumberGutter({ ast, direction, visible, onGoToBlock }: LineNumberGutterProps) {
  const layerRef = useRef<HTMLDivElement>(null)
  const numberElsRef = useRef<Map<string, HTMLDivElement>>(new Map())
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Only compute when the gutter is on — keeps it at zero cost while toggled off.
  const lineInfos = useMemo(() => (visible && ast ? computeBlockLineNumbers(ast) : []), [visible, ast])
  const lineInfosRef = useRef<BlockLineInfo[]>(lineInfos)
  lineInfosRef.current = lineInfos

  const reposition = useCallback(() => {
    const layer = layerRef.current
    const existing = numberElsRef.current

    const clearAll = () => {
      for (const el of existing.values()) {
        el.remove()
      }
      existing.clear()
    }

    // The editor lives in the same page Box this layer is mounted in.
    const page = layer?.parentElement
    const contentEl = page?.querySelector('.lexical-editor-content') as HTMLElement | null
    if (!visible || !layer || !page || !contentEl) {
      clearAll()
      return
    }

    const isRTL = direction === 'rtl'
    const layerRect = layer.getBoundingClientRect()
    const contentRect = contentEl.getBoundingClientRect()

    // The gutter sits in the leading-side margin. If it's too narrow (mobile),
    // hide rather than overflow into the text. Auto-adapts to any layout.
    const sidePadding = isRTL ? layerRect.right - contentRect.right : contentRect.left - layerRect.left
    if (sidePadding < GUTTER_WIDTH + GUTTER_GAP) {
      clearAll()
      return
    }

    // Fixed horizontal offset for every row → a straight column just outside the text.
    const columnLeft = isRTL
      ? contentRect.right - layerRect.left + GUTTER_GAP
      : contentRect.left - layerRect.left - GUTTER_GAP - GUTTER_WIDTH

    const startById = new Map<string, number>()
    for (const info of lineInfosRef.current) {
      startById.set(info.id, info.startLine)
    }

    const seen = new Set<string>()
    contentEl.querySelectorAll('[data-block-id]').forEach((el) => {
      const blockId = el.getAttribute('data-block-id')
      if (!blockId) {
        return
      }
      const startLine = startById.get(blockId)
      if (startLine === undefined) {
        return
      }
      seen.add(blockId)

      // `top` is scroll-invariant: block and layer scroll together inside the page.
      const rect = (el as HTMLElement).getBoundingClientRect()
      const top = rect.top - layerRect.top

      let numEl = existing.get(blockId)
      if (!numEl) {
        numEl = document.createElement('div')
        numEl.className = NUMBER_CLASS
        layer.appendChild(numEl)
        existing.set(blockId, numEl)
      }
      numEl.textContent = String(startLine)
      numEl.style.top = `${top}px`
      numEl.style.left = `${columnLeft}px`
      numEl.style.width = `${GUTTER_WIDTH}px`
      // Hug the text edge: left-aligned for a right gutter, right-aligned for a left gutter.
      numEl.style.textAlign = isRTL ? 'left' : 'right'
    })

    for (const [id, el] of existing) {
      if (!seen.has(id)) {
        el.remove()
        existing.delete(id)
      }
    }
  }, [visible, direction])

  const scheduleReposition = useCallback(() => {
    if (debounceRef.current !== null) {
      clearTimeout(debounceRef.current)
    }
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null
      reposition()
    }, REPOSITION_DEBOUNCE_MS)
  }, [reposition])

  useEffect(() => {
    if (visible) {
      injectStyles()
    }
  }, [visible])

  // Recompute when content, direction, or visibility changes.
  useEffect(() => {
    scheduleReposition()
  }, [lineInfos, direction, visible, scheduleReposition])

  // Reflow (typing grows the page, viewport resizes) shifts block tops without a
  // scroll event — watch the page element and the window.
  useEffect(() => {
    const page = layerRef.current?.parentElement
    if (!visible || !page) {
      return
    }
    const observer = new ResizeObserver(() => {
      scheduleReposition()
    })
    observer.observe(page)
    window.addEventListener('resize', scheduleReposition)
    return () => {
      observer.disconnect()
      window.removeEventListener('resize', scheduleReposition)
    }
  }, [visible, scheduleReposition])

  // Clean up on unmount.
  useEffect(() => {
    const els = numberElsRef.current
    return () => {
      if (debounceRef.current !== null) {
        clearTimeout(debounceRef.current)
      }
      for (const el of els.values()) {
        el.remove()
      }
      els.clear()
    }
  }, [])

  // ---- Go to line (Cmd/Ctrl+G) ----------------------------------------------
  const [goToOpen, setGoToOpen] = useState(false)
  const [goToValue, setGoToValue] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!visible) {
      return
    }
    const handler = (event: KeyboardEvent) => {
      const isModifier = event.metaKey || event.ctrlKey
      if (isModifier && !event.shiftKey && !event.altKey && (event.key === 'g' || event.key === 'G')) {
        event.preventDefault()
        setGoToValue('')
        setGoToOpen(true)
      }
    }
    document.addEventListener('keydown', handler)
    return () => {
      document.removeEventListener('keydown', handler)
    }
  }, [visible])

  const handleGoTo = useCallback(() => {
    const target = parseInt(goToValue, 10)
    setGoToOpen(false)
    if (isNaN(target)) {
      return
    }
    const infos = lineInfosRef.current
    if (infos.length === 0) {
      return
    }
    // The block whose markdown contains the line; else the nearest following
    // block (a line on a blank separator / in the frontmatter); else the last.
    const match =
      infos.find((info) => target >= info.startLine && target <= info.endLine) ||
      infos.find((info) => info.startLine >= target) ||
      infos[infos.length - 1]
    onGoToBlock(match.id)
  }, [goToValue, onGoToBlock])

  return (
    <>
      <div ref={layerRef} aria-hidden style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 2 }} />
      <Dialog
        open={goToOpen}
        onClose={() => setGoToOpen(false)}
        maxWidth="xs"
        // Don't return focus to the editor on close — refocusing the contentEditable
        // scrolls its cursor back into view and would undo the jump.
        disableRestoreFocus
        // autoFocus on the field is unreliable under the focus-trap; focus once the
        // open transition has finished instead.
        TransitionProps={{ onEntered: () => inputRef.current?.focus() }}
      >
        <DialogTitle>Go to line</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            inputRef={inputRef}
            fullWidth
            type="number"
            variant="standard"
            placeholder="Line number"
            value={goToValue}
            onChange={(e) => setGoToValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                handleGoTo()
              }
            }}
            inputProps={{ min: 1, dir: 'ltr', style: { textAlign: 'left' } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGoToOpen(false)}>Cancel</Button>
          <Button onClick={handleGoTo} variant="contained">
            Go
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
